import { supabase } from '@/lib/supabase'
import { fetchAllPaged } from '@/lib/fetchAllPaged'
import type { StageRoomIds } from '@/lib/coldroomReportRows'

// The active-dolly read, shared by the Coldroom register and the Coldroom map
// (F05 UF-06) under ONE cache key, so the map's "+ N dollies" card and the
// register's dolly rows are the same read and can never disagree.
export const ACTIVE_COOLING_BATCHES_KEY = ['coldroom-report-cooling-batches'] as const

// RELEASED dollies (signed off, waiting for Tipping) ride the same read so the
// map's card can count them (Ruled 8 Oct, as the mock-up). They are NOT in the
// register's room totals: it keeps only COOLING / IN_HOLDING (see
// ColdroomReport `batchGroups`).
// ---------------------------------------------------------------------------
// Cooling batches (force-cool flows only) — audit FC-20: while a batch is
// COOLING or IN_HOLDING its dollies physically occupy a coldroom, but the
// register only listed pallets + bins, so the force-cool and holding rooms
// printed EMPTY while full. Which room a batch occupies is derived client-side:
// COOLING → the batch's own cooling_coldroom_id (per-batch force-cool room,
// mig 20260728173529 — set when a lane has ≥2 allocated coolers, exactly when
// the stage mapping below collapses to nothing), falling back to the per-tenant
// cooling_stage_rooms mapping for the stage; IN_HOLDING → the batch's own
// holding_coldroom_id snapshot (mig 20260714091523, not in generated types yet
// — cast below), falling back to the IN_HOLDING stage mapping; no room
// resolvable → an explicit 'Unmapped' bucket so the fruit never silently
// disappears from the report again.
// ---------------------------------------------------------------------------
export interface CoolingBatchRow {
  batch_id:            string
  batch_number:        string
  status:              'COOLING' | 'IN_HOLDING' | 'RELEASED'
  crate_count:         number
  net_weight_kg:       number | null
  variety:             string | null
  cool_started_at:     string | null
  cool_ended_at:       string | null
  cooling_coldroom_id: string | null
  holding_coldroom_id: string | null
  created_at:          string
  // The dolly's CHANNEL, for per-lane dwell tints (FCX-26 M2b). Plain embed →
  // null on a legacy/out-of-scope row → the site's default lane.
  deliveries:          { commodity_id: string | null } | { commodity_id: string | null }[] | null
}

// (two parallel reads + a straight-line merge)
// fallow-ignore-next-line complexity
export async function fetchActiveCoolingBatches(): Promise<{ batches: CoolingBatchRow[]; stageRoomIds: StageRoomIds }> {
  // Two plain reads client-merged (not a nested embed) — same shape as the
  // Precool board, so a cross-tenant embed can never silently drop rows.
  const [{ rows, error }, mapRes] = await Promise.all([
    fetchAllPaged<CoolingBatchRow>((from, to) =>
      supabase
        .from('cooling_batches')
        .select(`batch_id, batch_number, status, crate_count, net_weight_kg, variety,
                 cool_started_at, cool_ended_at, cooling_coldroom_id, holding_coldroom_id, created_at,
                 deliveries(commodity_id)`)
        .in('status', ['COOLING', 'IN_HOLDING', 'RELEASED'])
        .order('created_at', { ascending: true }).order('batch_id')
        .range(from, to) as unknown as PromiseLike<{ data: CoolingBatchRow[] | null; error: { message: string } | null }>,
    ),
    supabase.from('cooling_stage_rooms').select('flow_key, stage, coldroom_id'),
  ])
  if (error) throw new Error(error)
  if (mapRes.error) throw mapRes.error
  // FCX-26 M2 — the mapping is per CHANNEL now (mig 20260715190000). This
  // report groups every channel's batches together, so a stage that ONE channel
  // maps is unambiguous, but a stage TWO channels map has no single answer:
  // collapse to a room only when every channel agrees, else leave it unset and
  // let the batch's own holding snapshot / the Unmapped bucket speak. Guessing
  // here is exactly the bug the re-key fixes (berry fruit shown in the other
  // berry's room).
  const seen: Record<string, Set<string>> = {}
  for (const r of (mapRes.data ?? []) as { flow_key: string; stage: string; coldroom_id: string }[]) {
    if (r.stage !== 'COOLING' && r.stage !== 'IN_HOLDING') continue
    ;(seen[r.stage] ??= new Set()).add(r.coldroom_id)
  }
  const stageRoomIds: StageRoomIds = {}
  for (const stage of ['COOLING', 'IN_HOLDING'] as const) {
    const ids = seen[stage]
    if (ids && ids.size === 1) stageRoomIds[stage] = [...ids][0]
  }
  return { batches: rows, stageRoomIds }
}
